import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Layout from '../components/Layout';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  IconButton, Switch, FormControlLabel, Tooltip, CircularProgress,
  Alert, Chip, Grid, Avatar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadIcon from '@mui/icons-material/Upload';
import ClearIcon from '@mui/icons-material/Clear';

const DEFAULT_TILES = [
  { key: 'junior',      icon: '🏐', label: 'Junior Training',        desc: 'Skill development programs for youth athletes of all levels.',  path: '/junior-training',                       external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'adult',       icon: '🌊', label: 'Adult Training',          desc: 'Competitive and recreational sessions for adult players.',       path: '/adult-training',                        external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'tournaments', icon: '🏆', label: 'Tournament Registration', desc: 'Sign up for upcoming beach volleyball tournaments.',             path: '/tournaments',                           external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'league',      icon: '🗓', label: 'League Registration',     desc: 'Join a recreational or competitive league.',                    path: '',                                       external: false, enabled: false, image_data: '', image_mime: '' },
  { key: 'membership',  icon: '⭐', label: 'Memberships',             desc: 'Flexible membership plans for regular adult players.',          path: '/adult-membership',                      external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'streaming',   icon: '📺', label: 'Live Streaming',          desc: 'Watch live and on-demand tournament coverage.',                 path: 'https://www.sporfie.com/torchvolleyball', external: true,  enabled: true,  image_data: '', image_mime: '' },
];

export default function DashboardTiles() {
  const navigate = useNavigate();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);
  const fileRefs = useRef([]);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'settings', 'dashboardTiles'));
        if (snap.exists()) {
          const data = snap.data();
          if (data.tiles?.length > 0) {
            // Ensure image fields exist on old tiles
            setTiles(data.tiles.map(t => ({ image_data: '', image_mime: '', ...t })));
          } else {
            setTiles(DEFAULT_TILES);
          }
          setHeaderText(data.headerText || '');
        } else {
          setTiles(DEFAULT_TILES);
        }
      } catch(e) { console.error(e); setTiles(DEFAULT_TILES); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function updateTile(idx, field, value) {
    setTiles(t => t.map((tile, i) => i === idx ? { ...tile, [field]: value } : tile));
  }

  function handleImageUpload(idx, e) {
    const file = e.target.files[0];
    if (!file) return;
    // Warn if file is large
    if (file.size > 200 * 1024) {
      if (!window.confirm(`This image is ${Math.round(file.size/1024)}KB. Large images may slow the dashboard. Continue?`)) return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target.result;
      const [header, b64] = dataUrl.split(',');
      const mime = header.replace('data:', '').replace(';base64', '');
      setTiles(t => t.map((tile, i) => i === idx ? { ...tile, image_data: b64, image_mime: mime } : tile));
    };
    reader.readAsDataURL(file);
  }

  function clearImage(idx) {
    setTiles(t => t.map((tile, i) => i === idx ? { ...tile, image_data: '', image_mime: '' } : tile));
  }

  function addTile() {
    setTiles(t => [...t, {
      key: `tile_${Date.now()}`,
      icon: '🏐', label: 'New Tile', desc: '', path: '',
      external: false, enabled: true, image_data: '', image_mime: '',
    }]);
  }

  function removeTile(idx) {
    if (!window.confirm('Remove this tile?')) return;
    setTiles(t => t.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'dashboardTiles'), { tiles, headerText });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  function resetToDefaults() {
    if (!window.confirm('Reset all tiles to defaults?')) return;
    setTiles(DEFAULT_TILES);
  }

  function onDragStart(idx) { dragIdx.current = idx; }
  function onDragOver(e, idx) { e.preventDefault(); dragOverIdx.current = idx; }
  function onDrop() {
    const from = dragIdx.current;
    const to = dragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const updated = [...tiles];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setTiles(updated);
    dragIdx.current = null;
    dragOverIdx.current = null;
  }

  if (loading) return (
    <Layout>
      <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
    </Layout>
  );

  return (
    <Layout>
      <Box sx={{ bgcolor: '#1E3A5F', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">🏠 Dashboard Tile Management</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                Manage tiles shown on the public landing page. Images override emoji icons.
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate('/?dashboard=1')}
              sx={{ borderColor: '#C9A84C', color: '#C9A84C', fontWeight: 600,
                '&:hover': { borderColor: '#e0be6a', color: '#e0be6a', backgroundColor: 'rgba(201,168,76,0.08)' } }}>
              Preview Landing Page
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 6 }}>
        {saved && <Alert severity="success" sx={{ mb: 3 }}>Tiles saved! Changes are live immediately.</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Drag tiles to reorder. Upload an image to replace the emoji icon. Recommended image size: under 200KB, square format.
        </Alert>

        <Card elevation={2} sx={{ borderRadius: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Dashboard Text Block
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Shown on the landing page just below the gold stripe. Leave blank to hide it entirely. Blank lines create paragraph breaks.
            </Typography>
            <TextField
              fullWidth multiline minRows={4}
              placeholder="e.g. Year-round junior beach volleyball training in East Orlando. Six lighted courts, flexible scheduling — start anytime."
              value={headerText}
              onChange={e => setHeaderText(e.target.value)}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {tiles.map((tile, idx) => (
            <Card key={tile.key} elevation={2} sx={{
              borderRadius: 3, opacity: tile.enabled ? 1 : 0.65,
              border: '1px solid', borderColor: tile.enabled ? 'primary.main' : 'divider',
            }}
              draggable onDragStart={() => onDragStart(idx)}
              onDragOver={e => onDragOver(e, idx)} onDrop={onDrop}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2} alignItems="flex-start">

                  {/* Drag handle */}
                  <Grid sx={{ display: 'flex', alignItems: 'center', pt: '20px !important' }}>
                    <DragIndicatorIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
                  </Grid>

                  {/* Image / Icon preview + upload */}
                  <Grid sx={{ pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      {/* Preview */}
                      <Avatar
                        src={tile.image_data ? `data:${tile.image_mime};base64,${tile.image_data}` : undefined}
                        sx={{ width: 56, height: 56, bgcolor: 'grey.100', fontSize: 28, border: '1px solid', borderColor: 'divider' }}
                      >
                        {!tile.image_data && tile.icon}
                      </Avatar>

                      {/* Upload button */}
                      <input
                        type="file" accept="image/*"
                        style={{ display: 'none' }}
                        ref={el => fileRefs.current[idx] = el}
                        onChange={e => handleImageUpload(idx, e)}
                      />
                      <Button size="small" variant="outlined" startIcon={<UploadIcon />}
                              sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
                              onClick={() => fileRefs.current[idx]?.click()}>
                        Image
                      </Button>
                      {tile.image_data && (
                        <Button size="small" color="error" startIcon={<ClearIcon />}
                                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
                                onClick={() => clearImage(idx)}>
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Grid>

                  {/* Emoji fallback */}
                  <Grid sx={{ pt: '20px !important' }}>
                    <TextField size="small" label="Emoji" value={tile.icon}
                               onChange={e => updateTile(idx, 'icon', e.target.value)}
                               inputProps={{ style: { fontSize: 22, textAlign: 'center' } }}
                               sx={{ width: 72 }}
                               helperText="Fallback" />
                  </Grid>

                  {/* Label + Desc + URL */}
                  <Grid size="grow">
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField fullWidth size="small" label="Label" value={tile.label}
                                   onChange={e => updateTile(idx, 'label', e.target.value)} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 7 }}>
                        <TextField fullWidth size="small" label="Description" value={tile.desc}
                                   onChange={e => updateTile(idx, 'desc', e.target.value)} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField fullWidth size="small" label="URL / Path" value={tile.path}
                                   placeholder={tile.external ? 'https://...' : '/page-name'}
                                   onChange={e => updateTile(idx, 'path', e.target.value)}
                                   InputProps={{ endAdornment: tile.external ? <OpenInNewIcon fontSize="small" sx={{ color: 'text.disabled' }} /> : null }} />
                      </Grid>
                      <Grid size={{ xs: 4, sm: 1.5 }}>
                        <FormControlLabel
                          control={<Switch size="small" checked={tile.external}
                                          onChange={e => updateTile(idx, 'external', e.target.checked)} />}
                          label={<Typography variant="caption">External</Typography>}
                          sx={{ m: 0 }} />
                      </Grid>
                      <Grid size={{ xs: 4, sm: 2.5 }}>
                        <FormControlLabel
                          control={<Switch size="small" checked={tile.enabled}
                                          onChange={e => updateTile(idx, 'enabled', e.target.checked)} />}
                          label={<Typography variant="caption" color={tile.enabled ? 'success.main' : 'text.disabled'}>
                            {tile.enabled ? 'Active' : 'Coming Soon'}
                          </Typography>}
                          sx={{ m: 0 }} />
                      </Grid>
                    </Grid>

                    {/* Status chips */}
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      {!tile.enabled && <Chip label="Coming Soon" size="small" color="warning" />}
                      {tile.external && <Chip label="Opens in new tab" size="small" color="info" />}
                      {!tile.path && tile.enabled && <Chip label="No URL set" size="small" color="error" />}
                      {tile.image_data && <Chip label="Custom image" size="small" color="success" />}
                    </Box>
                  </Grid>

                  {/* Delete */}
                  <Grid sx={{ pt: '16px !important' }}>
                    <Tooltip title="Remove tile">
                      <IconButton size="small" color="error" onClick={() => removeTile(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={saving ? null : <SaveIcon />}
                  onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Tiles'}
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addTile}>Add Tile</Button>
          <Button variant="outlined" color="warning" onClick={resetToDefaults}>Reset to Defaults</Button>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/settings')}>Back</Button>
        </Box>
      </Container>
    </Layout>
  );
}
